import { Film } from 'lucide-react'
import { useConsent } from '@/features/consent'
import { cn } from '@/lib/utils'

interface FilmThumbnailProps {
  title: string
  youtubeId?: string
  thumbnailUrl?: string
  className?: string
}

function isYoutubeImage(url: string): boolean {
  try {
    const hostname = new URL(url).hostname
    return hostname === 'img.youtube.com' || hostname === 'i.ytimg.com'
  } catch {
    return false
  }
}

export function FilmThumbnail({ title, youtubeId, thumbnailUrl, className }: FilmThumbnailProps) {
  const { consent } = useConsent()
  const mayLoadThumbnail = Boolean(
    thumbnailUrl && (!isYoutubeImage(thumbnailUrl) || consent?.media),
  )
  const youtubeThumbnail = consent?.media && youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
    : null
  const src = mayLoadThumbnail ? thumbnailUrl : youtubeThumbnail

  if (!src) {
    return (
      <div
        role="img"
        aria-label={`${title} film artwork`}
        className={cn(
          'absolute inset-0 flex items-center justify-center bg-linear-to-br from-brand-purple-dim via-brand-black to-brand-blue-dim text-brand-white',
          className,
        )}
      >
        <div className="text-center">
          <Film className="mx-auto h-8 w-8 text-brand-purple" aria-hidden="true" />
          <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-brand-white/70">
            One Day Dance Film
          </p>
        </div>
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={title}
      className={className}
      onError={(event) => {
        if (!thumbnailUrl && consent?.media && youtubeId) {
          event.currentTarget.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
        }
      }}
    />
  )
}
